<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { defaultColour, findColour } from "$lib/data/colours.data";
  import type { colourVariant } from "$lib/data/colours.data";
  import ColourInner from "./ColourInner.svelte";
  import { _ } from "svelte-i18n";
  import Picture from "$lib/components/Picture/Picture.svelte";

  export let colourOption: colourVariant;
  export let selected = false;
  export let inStock = true;
  export let label = "";
  export let count = 0;
  export let small = false;
  export let href = "";
  export let plain = false;
  export let disabled = false;
  export let rounded = true;
  export let withBorder: boolean = false;

  const imageInputConfig = {
    smSize: { width: 52, height: 64 },
    mdSize: { width: 64, height: 80 },
    lgSize: { width: 72, height: 90 },
    xlSize: { width: 72, height: 90 },
  };

  let hovered = false;
  let outlineColour = defaultColour;

  $: if (colourOption) {
    outlineColour = findColour(colourOption.identifier);
  }

  const dispatch = createEventDispatcher();

  const selectColour = () => {
    dispatch("select", colourOption);
  };

  const isWhiteOrClose = hexColor => {
    hexColor = hexColor.replace("#", "");
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    const threshold = 30; // Adjust this threshold if needed

    return r > 255 - threshold && g > 255 - threshold && b > 255 - threshold;
  };
</script>

<style lang="scss">
  .colour {
    align-items: center;
    background: none;
    border: 1px solid transparent;
    display: flex;
    padding: 0;
    position: relative;
    width: fit-content;
    z-index: 5;
    cursor: inherit;

    &--rounded {
      border-radius: 50%;
    }

    &--pointer {
      cursor: pointer;
    }

    &--image {
      padding: 4px;
      transition: border 0.2s ease;
      width: 62px;
      @include breakpoint(md) {
        // width: 74px;
      }
      @include breakpoint(lg) {
        // width: 82px;
      }
      &:hover {
        border: 1px solid $on-surface-30;
      }
    }

    & > :global(.inner) {
      width: 52px;

      @include breakpoint(md) {
        width: 64px;
      }

      @include breakpoint(lg) {
        width: 72px;
      }
    }

    &--selected {
      border: 1px solid;
      border-radius: 50%;

      &__rounded {
        border-radius: 50%;
      }

      &--image {
        border-radius: 0;
        border: 1px solid $on-surface-30;
      }
    }

    &--small {
      margin: 0;
    }
  }
</style>

{#if colourOption}
  {#if href}
    <a
      on:mouseenter={() => (hovered = true)}
      on:mouseleave={() => (hovered = false)}
      {href}
      class={`colour colour--rounded
    ${selected && "colour--selected"}
    ${small ? "colour--small" : ""}
    ${disabled ? "colour--disabled" : "colour--pointer"}
    ${rounded && "colour--pointer__rounded colour--selected__rounded"}
    ${hovered && "colour--hovered"}
    `}
      style={selected && outlineColour && !isWhiteOrClose(outlineColour.hex)
        ? `border-color: ${outlineColour.hex}; `
        : (selected && !outlineColour) ||
            (selected && outlineColour && !!isWhiteOrClose(outlineColour.hex))
          ? `border-color: var(--on-surface-30);`
          : hovered && !isWhiteOrClose(outlineColour.hex)
            ? `border-color: ${outlineColour.hex};`
            : hovered && !!isWhiteOrClose(outlineColour.hex)
              ? `border-color: var(--on-surface-30);`
              : disabled
                ? `border-color: none;`
                : ""}
    >
      <ColourInner
        {colourOption}
        {selected}
        {inStock}
        {label}
        {count}
        {plain}
        {rounded}
        {withBorder}
      />
    </a>
  {:else if plain}
    <ColourInner
      class={`${$$props.class}`}
      {colourOption}
      {selected}
      {inStock}
      {label}
      {count}
      {plain}
      {rounded}
      {withBorder}
    />
  {:else if colourOption.image}
    <button
      on:mouseenter={() => (hovered = true)}
      on:mouseleave={() => (hovered = false)}
      class={`colour colour--image
      ${selected && "colour--selected colour--selected--image"}
      ${disabled ? "colour--disabled" : "colour--pointer"}
      `}
      on:click={selectColour}
      aria-label={`${$_("Colour")} - ${colourOption.identifier}`}
    >
      <Picture
        altText={colourOption.identifier}
        imageConfig={imageInputConfig}
        shopifyImage
        smImage={colourOption.image}
        mdImage={colourOption.image}
      />
    </button>
  {:else}
    <button
      on:mouseenter={() => (hovered = true)}
      on:mouseleave={() => (hovered = false)}
      class={`colour
      ${selected && "colour--selected"}
      ${small ? "colour--small" : ""}
      ${disabled ? "colour--disabled" : "colour--pointer"}
      ${
        rounded &&
        "colour--rounded colour--pointer__rounded colour--selected__rounded"
      }
      `}
      style={selected && outlineColour && !isWhiteOrClose(outlineColour.hex)
        ? `border-color: ${outlineColour.hex}; `
        : (selected && !outlineColour) ||
            (selected && outlineColour && !!isWhiteOrClose(outlineColour.hex))
          ? `border-color: var(--on-surface-30);`
          : hovered && !isWhiteOrClose(outlineColour.hex)
            ? `border-color: ${outlineColour.hex};`
            : hovered && !!isWhiteOrClose(outlineColour.hex)
              ? `border-color: var(--on-surface-30);`
              : disabled
                ? `border-color: none;`
                : ""}
      on:click|preventDefault={selectColour}
      aria-label={`${$_("Colour")} - ${colourOption.identifier}`}
    >
      <ColourInner
        {colourOption}
        {selected}
        {inStock}
        {label}
        {count}
        {plain}
        {rounded}
      />
    </button>
  {/if}
{/if}
